// @flow

/**
 * simpals-id consent sync helpers.
 *
 * The sync is WRITE-ONLY by design. Consent to store on the device is per
 * terminal equipment (ePrivacy art. 5(3)), so the local cc_consent cookie is the
 * only runtime source of truth and every new device asks again. The server
 * record exists solely to demonstrate consent for an identified user (GDPR
 * art. 7(1)) — it is never read back to pre-fill a device, which would apply a
 * choice made on one device to another one that never consented.
 *
 * Pure helpers only — the transport lives in @simpals/next-simpalsbar
 * (updateConsents), the wiring in index.js.
 *
 * Note: the record is written under THIS app's simpals-id project
 * (src/constants → PROJECT_ID, `…7099`), which is a different project from
 * 999.md's (`…709b`). The two consent records are independent by design.
 */

import { PROJECT_ID } from '../../constants';

import {
  COOKIE_POLICY_PATH,
  LEGAL_ORIGIN,
  POLICY_VERSION,
  PRIVACY_POLICY_PATH,
  TERMS_PATH,
} from './constants';

/** Prefix of the per-account sessionStorage flag set by consentSyncFlagFor(). */
export const CONSENT_SYNC_SESSION_FLAG = 'cc_synced';

/**
 * `chosenBy` stamp value for a choice made while logged out (also the implied
 * value for legacy cc_consent cookies written before stamping existed).
 */
export const CONSENT_ANONYMOUS_ID = 'anon';

/**
 * sessionStorage flag: server sync already ran for this account in this browser
 * session. Scoped per account so switching accounts within one session still
 * covers the new account (a global flag would silently skip it).
 */
export function consentSyncFlagFor(accountId: string): string {
  return `${CONSENT_SYNC_SESSION_FLAG}_${accountId}`;
}

/**
 * Whether the device's local choice was made by a DIFFERENT logged-in account
 * (read from the `chosenBy` stamp in the cc_consent cookie's data field). A
 * foreign choice must never be pushed into this account's server record — that
 * would fabricate consent evidence. Anonymous and unstamped (legacy) choices
 * are NOT foreign: they were most likely made by the person now logging in.
 */
export function isForeignConsent(chosenBy: mixed, accountId: string): boolean {
  return (
    typeof chosenBy === 'string' &&
    !!chosenBy &&
    chosenBy !== CONSENT_ANONYMOUS_ID &&
    chosenBy !== accountId
  );
}

/** Single source of truth for the simpals-id projectId used by consent sync calls. */
export const CONSENT_PROJECT_ID = PROJECT_ID;

/**
 * Banner category → simpals-id `ConsentKey` enum value. `necessary` is absent on
 * purpose: the backend enum models only real (optional) consents — mandatory
 * cookies are not a consent and cannot be sent. There is no MARKETING_COOKIE
 * entry because this app has no advertising category (see consentMode.js).
 */
export const CONSENT_KEY_BY_CATEGORY = {
  functionality: 'FUNCTIONAL_COOKIE',
  analytics: 'ANALYTICS_COOKIE',
};

/** Category → the messages.js keys describing it in the banner. */
const CONSENT_COPY_BY_CATEGORY = {
  functionality: {
    title: 'catFunctionalityTitle',
    purpose: 'catFunctionalityDesc',
  },
  analytics: { title: 'catAnalyticsTitle', purpose: 'catAnalyticsDesc' },
};

const OPTIONAL_CATEGORIES = Object.keys(CONSENT_KEY_BY_CATEGORY);

function choiceFor(category: string, accepted: boolean, t: Function) {
  const copy = CONSENT_COPY_BY_CATEGORY[category];
  return {
    consentKey: CONSENT_KEY_BY_CATEGORY[category],
    status: accepted ? 'GRANTED' : 'DENIED',
    title: t(copy.title),
    purpose: t(copy.purpose),
    consentText: t('consentDescription'),
    requiresExplicitConsent: true,
    isRequiredForService: false,
    isUserConfigurable: true,
  };
}

/**
 * The backend requires the full document set on every write. Only the cookie
 * policy is versioned in this repo (POLICY_VERSION — it doubles as the banner
 * revision: bumping it re-prompts every user and the fresh choice is recorded
 * under the new version); the privacy policy and the site rules have no
 * versioning yet and are recorded as '1'.
 * TODO(legal): bump those alongside material changes to their pages.
 */
function buildDocuments(locale: string) {
  return {
    cookiePolicyVersion: String(POLICY_VERSION),
    cookiePolicyUrl: `${LEGAL_ORIGIN}/${locale}${COOKIE_POLICY_PATH}`,
    privacyPolicyVersion: '1',
    privacyPolicyUrl: `${LEGAL_ORIGIN}/${locale}${PRIVACY_POLICY_PATH}`,
    termsVersion: '1',
    termsUrl: `${LEGAL_ORIGIN}/${locale}${TERMS_PATH}`,
  };
}

/** Full updateConsents payload for the user's current banner choices. */
export function buildUpdateConsentsInput({
  acceptedCategories,
  locale,
  t,
}: {
  acceptedCategories: string[],
  locale: string,
  t: Function,
}) {
  return {
    projectId: CONSENT_PROJECT_ID,
    locale,
    documents: buildDocuments(locale),
    choices: OPTIONAL_CATEGORIES.map(category =>
      choiceFor(category, acceptedCategories.includes(category), t),
    ),
  };
}
